// /brokers/receivables — the broker-to-exchange receivable case list
// (SM-771, SM-621 phase 06b). The console's first broker screen; the
// full broker portal and risk views are 08a's.
//
// ── what a receivable is ─────────────────────────────────────────────
//
// A THIRD_PARTY_CREDIT broker's users trade on credit the broker
// extends; the exchange pays their winnings from its own ledger and
// settles with the broker afterwards (B12). When a tree's settled losses
// exceed the collateral the broker put up, broker-service's shortfall
// worker opens ONE receivable per root and currency for the uncovered
// part, with the evidence it measured. It is never revenue and never
// ledger money (B4); while it is unresolved every new BUY in that tree
// is refused, and the ops exposure check alerts on it.
//
// ── one queue ────────────────────────────────────────────────────────
//
// Same reasoning as the payout queue (SM-308) and the dispute queue
// (SM-320): the default view is what needs a human — the UNRESOLVED
// ones (OPEN + ACKNOWLEDGED) — and the state filter widens it to
// history. Filters and the open panel live in the URL so a triage view
// is shareable and a decision linkable.
//
// The route is /brokers/…, deliberately NOT under /admin: the dev
// server proxies the /admin prefix to the API, and a console page
// there would be swallowed by it.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";

import { ReceivableDetail } from "@/components/brokers/ReceivableDetail";
import { EXPOSURE_CURRENCY } from "@/components/brokers/governance";
import {
  canonToWire,
  fmtDuration,
  isAged,
  pendingAction,
  receivableAgeMs,
  receivableStateLabel,
  receivableStateTone,
  actionKindLabel,
} from "@/components/brokers/receivables";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { adminName, useAdminMap } from "@/hooks/useAdminNames";
import { useMyPerms } from "@/hooks/useMyPerms";
import { brokerKeys, fetchBrokerReceivables, fetchBrokerSummary } from "@/lib/broker-queries";
import {
  RECEIVABLE_STATES,
  RECEIVABLE_UNRESOLVED,
  type ReceivableItem,
} from "@/lib/broker-types";
import { cn } from "@/lib/cn";
import { fmtDate } from "@/lib/format";
import { Perm } from "@/lib/perms";

const PAGE_SIZE = 20;

/** ?state=ALL widens to every state; absent means the unresolved default. */
const ANY_STATE = "ALL";

interface Search {
  state?: string;
  broker?: string;
  currency?: string;
  page?: number;
  /** The receivable whose panel is open. */
  open?: string;
}

const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

function parseState(v: unknown): string | undefined {
  if (v === ANY_STATE || v === RECEIVABLE_UNRESOLVED) return v;
  return RECEIVABLE_STATES.find((s) => s === v);
}

export const Route = createFileRoute("/_app/brokers/receivables")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    state: parseState(s.state),
    broker: str(s.broker),
    currency: CURRENCIES.includes(String(s.currency)) ? String(s.currency) : undefined,
    page: Number.isFinite(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
    open: str(s.open),
  }),
  component: BrokerReceivablesPage,
});

const th = "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

// Currency codes are identifiers, not copy: the option shows the code.
// A receivable is raised against exposure, which exists in GOLD only
// (SM-768 B7) — RED or GREEN would only ever list nothing (SM-848), and
// an old link's ?currency=RED is dropped rather than obeyed.
const CURRENCIES: string[] = [EXPOSURE_CURRENCY];
const CURRENCY_OPTIONS = CURRENCIES.map((c) => ({ value: c, label: c }));

const STATE_OPTIONS = [
  { value: RECEIVABLE_UNRESOLVED, label: "Unresolved (open or acknowledged)" },
  { value: "OPEN", label: "Open" },
  { value: "ACKNOWLEDGED", label: "Acknowledged" },
  { value: "SETTLED", label: "Settled" },
  { value: "WRITTEN_OFF", label: "Written off" },
  { value: ANY_STATE, label: "Any state" },
];

function BrokerReceivablesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const admins = useAdminMap();
  const canView = perms.has(Perm.BrokerExposureView);

  const stateFilter = search.state ?? RECEIVABLE_UNRESOLVED;
  const page = search.page ?? 0;
  const filter = {
    state: stateFilter === ANY_STATE ? undefined : stateFilter,
    broker_id: search.broker,
    currency: search.currency,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const q = useQuery({
    queryKey: brokerKeys.receivables(filter),
    queryFn: () => fetchBrokerReceivables(filter),
    // The shortfall worker reassesses every minute; a receivable a
    // colleague just took should not still read as unowned here.
    refetchInterval: 30_000,
    enabled: canView,
  });
  const rows = q.data?.items ?? [];

  // Name each root. One read per distinct broker on the page (a root has
  // at most one unresolved receivable per currency, so the set is
  // small), cached, and only for an admin who may read brokers at all —
  // without BROKER_VIEW the id stands on its own.
  const canNameBrokers = perms.has(Perm.BrokerView);
  const brokerIDs = [...new Set(rows.map((r) => r.broker_id))];
  const names = useQueries({
    queries: brokerIDs.map((id) => ({
      queryKey: brokerKeys.broker(id),
      queryFn: () => fetchBrokerSummary(id),
      enabled: canNameBrokers,
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });
  const brokerName = new Map<string, string>();
  names.forEach((n, i) => {
    const name = n.data?.display_name || n.data?.legal_name;
    if (name) brokerName.set(brokerIDs[i], name);
  });

  function apply(next: Partial<Search>) {
    void navigate({ search: (s) => ({ ...s, ...next, page: undefined }) });
  }
  function openPanel(id: string | undefined) {
    void navigate({ search: (s) => ({ ...s, open: id }) });
  }

  if (perms.loaded && !canView) {
    return (
      <EmptyState
        title="No access"
        description="You need the BROKER_EXPOSURE_VIEW permission to see broker receivables."
      />
    );
  }

  const now = Date.now();
  const narrowed = stateFilter !== RECEIVABLE_UNRESOLVED || Boolean(search.broker || search.currency);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Broker receivables</h1>
        <p className="text-sm text-muted">
          What a broker tree owes the exchange once its users&apos; settled losses exceed the collateral the
          broker put up. While one is unresolved, new BUY orders anywhere in that tree are refused; cancels,
          sells, settlement and payouts carry on. Resolving one never moves ledger money.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <Select
            label="State"
            value={stateFilter}
            onChange={(e) => apply({ state: e.target.value === RECEIVABLE_UNRESOLVED ? undefined : e.target.value })}
            options={STATE_OPTIONS}
          />
        </div>
        <div className="w-56">
          <Input
            label="Broker"
            placeholder="Root broker id…"
            defaultValue={search.broker ?? ""}
            onKeyDown={(e) => {
              if (e.key === "Enter") apply({ broker: str((e.target as HTMLInputElement).value) });
            }}
            onBlur={(e) => apply({ broker: str(e.target.value) })}
          />
        </div>
        <div className="w-40">
          <Select
            label="Currency"
            value={search.currency ?? ""}
            onChange={(e) => apply({ currency: e.target.value || undefined })}
            placeholder="Any currency"
            options={CURRENCY_OPTIONS}
          />
        </div>
        {/* Its own line under the bar rather than the picker's hint: the
            filters are bottom-aligned, and a two-line control would drag
            every neighbour down with it. */}
        <p className="basis-full text-2xs text-muted">
          Only GOLD is listed: receivables come from third-party credit exposure, which exists in GOLD only.
        </p>
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Receivables</CardTitle>
          <span className="text-2xs text-muted">{q.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {(q.isLoading || !perms.loaded) && (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {q.isError && <ErrorPanel error={q.error} reset={() => void q.refetch()} />}
          {q.data && rows.length === 0 && (
            <p className="px-5 py-16 text-center text-sm text-muted">
              {narrowed
                ? "No receivables match the current filters."
                : "No broker owes the exchange anything right now. Settled and written-off receivables are under the state filter."}
            </p>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Broker</th>
                    <th className={th}>State</th>
                    <th className={th}>Currency</th>
                    <th className={`${th} text-right`}>Amount</th>
                    <th className={`${th} text-right`}>Peak</th>
                    <th className={th}>Owner</th>
                    <th className={th}>Opened</th>
                    <th className={th}>Age</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <ReceivableRow
                      key={r.receivable_id}
                      r={r}
                      name={brokerName.get(r.broker_id)}
                      owner={r.owner_admin_id ? adminName(admins, r.owner_admin_id) : undefined}
                      now={now}
                      onOpen={() => openPanel(r.receivable_id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {q.data && q.data.page.total > 0 && (
            <Pagination
              page={q.data.page}
              currentPage={page}
              onPageChange={(p) => void navigate({ search: (s) => ({ ...s, page: p > 0 ? p : undefined }) })}
              itemLabel="entries"
            />
          )}
        </CardBody>
      </Card>

      {search.open ? (
        <ReceivableDetail
          receivableID={search.open}
          fallback={rows.find((r) => r.receivable_id === search.open)}
          onClose={() => openPanel(undefined)}
        />
      ) : null}
    </div>
  );
}

function ReceivableRow({
  r,
  name,
  owner,
  now,
  onOpen,
}: {
  r: ReceivableItem;
  name?: string;
  owner?: string;
  now: number;
  onOpen: () => void;
}) {
  const pending = pendingAction(r);
  const aged = isAged(r, now);
  return (
    <tr
      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2/60"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
      tabIndex={0}
    >
      <td className={`${td} px-5`}>
        {name ? <div className="font-medium">{name}</div> : null}
        <div className="font-mono text-2xs text-muted">{r.broker_id}</div>
      </td>
      <td className={td}>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={receivableStateTone(r.state)}>{receivableStateLabel(r.state)}</Badge>
          {pending ? (
            <Badge tone="accent" title="Awaiting approval">
              {actionKindLabel(pending.kind)}
            </Badge>
          ) : null}
        </div>
      </td>
      <td className={td}>
        <CurrencyBadge currency={r.currency} />
      </td>
      <td className={`${td} text-right`}>
        <CurrencyAmount currency={r.currency} value={canonToWire(r.amount)} />
      </td>
      <td className={`${td} text-right text-muted`}>
        <CurrencyAmount currency={r.currency} value={canonToWire(r.peak_amount)} tone="muted" />
      </td>
      <td className={cn(td, !owner && r.state === "OPEN" && "text-warning")}>{owner ?? "Nobody yet"}</td>
      <td className={`${td} text-2xs text-muted`}>{fmtDate(new Date(r.opened_at_unix_ms).toISOString())}</td>
      <td className={cn(td, "text-2xs", aged ? "font-semibold text-warning" : "text-muted")}>
        {fmtDuration(receivableAgeMs(r, now))}
      </td>
    </tr>
  );
}
